import React from "react";
import { useDocumentCompliance } from "./hooks/useDocumentCompliance";

interface Props {
  username?: string;
  display: { DocumentCompliance: boolean };
}

const cardBase =
  "bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md p-4";

const DocumentComplianceSection: React.FC<Props> = ({ username, display }) => {
  const enabled = !!username && display.DocumentCompliance;
  const { missingESign, missingPostHire, loading } = useDocumentCompliance(username, enabled);

  if (!display.DocumentCompliance) return null;

  if (loading) {
    return (
      <div className="col-span-12">
        <div className={cardBase}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Document Compliance
          </h2>
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <div className="animate-spin inline-block w-5 h-5 border-4 border-blue-500 border-t-transparent rounded-full" />
            <span>Loading document compliance...</span>
          </div>
        </div>
      </div>
    );
  }

  const eSignComplete = missingESign.length === 0;
  const postHireComplete = missingPostHire.length === 0;

  return (
    <div className="col-span-12">
      <div className={cardBase}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Document Compliance
        </h2>

        <div className="space-y-4">
          {/* E-Sign Row */}
          <div className="flex flex-wrap items-center gap-3">
            {eSignComplete ? (
              <span className="text-green-600 dark:text-green-400 font-medium">
                ✔️ E-Sign Documents – Completed
              </span>
            ) : (
              <>
                <span className="text-orange-600 dark:text-orange-400 font-medium">
                  ❗ E-Sign Documents – {missingESign.length} Missing
                </span>
                <a
                  href="https://sharepoint.teampbs.com/sites/My/Pages/My-Esign-Documents.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded transition-colors"
                >
                  Required E-Sign
                </a>
              </>
            )}
          </div>

          {/* Post-Hire Row */}
          <div>
            <div className="flex flex-wrap items-center gap-3">
              {postHireComplete ? (
                <span className="text-green-600 dark:text-green-400 font-medium">
                  ✔️ Post-Hire Documents – Completed
                </span>
              ) : (
                <>
                  <span className="text-orange-600 dark:text-orange-400 font-medium">
                    ❗ Post-Hire Documents – {missingPostHire.length} Missing
                  </span>
                  <a
                    href="https://profile.teampbs.com/#/documents"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded transition-colors"
                  >
                    Upload Documents
                  </a>
                </>
              )}
            </div>

            {missingPostHire.length > 0 && (
              <ol className="mt-2 ml-1 space-y-1 text-sm text-gray-700 dark:text-gray-300 list-decimal list-inside">
                {missingPostHire.map((title, i) => (
                  <li key={i}>{title}</li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentComplianceSection;
